// Mock brand registry for Task #5 (Verticalization — Unified SuperAdmin).
//
// WHY THIS EXISTS:
//   Today name / logo / links / company address are hardcoded all over the
//   SuperAdmin UI, momsite and daoSource (see HARDCODED_BRAND_INVENTORY.md).
//   Once the 3 brands share one codebase, those literals must come from a
//   single source that returns the right values for the active vertical.
//
// WHAT THIS IS:
//   Mock data only. #5 feeds the brand helper from this file. Later (#26) the
//   helper will fetch the same shape from the real `/vertical/config` API that
//   Aminul builds in #11, and this file goes away. Keep the KEYS stable so the
//   swap is a drop-in.
//
// The keys 'circleram' / 'sn' / 'ssh' are the agreed vertical codes.
// NOTE: SN and SSH values below are PLACEHOLDERS — real values land with #11/#26.

export const DEFAULT_VERTICAL = 'circleram'

export const BRANDS = {
  circleram: {
    code: 'circleram',
    name: 'CircleRAM',
    legalName: 'CircleRAM AI',
    productName: 'CircleRAM Course Manager',
    superadminTitle: 'CircleRAM | Superadmin',
    logo: {
      // Paths are brand-relative; each deployment serves its own asset bundle.
      main: 'assets/images/CM-logo.png',
      white: 'assets/images/CM-logo-white.png',
      small: 'assets/images/logo-sm.png',
      favicon: 'assets/images/favicon.png'
    },
    links: {
      site: 'https://www.circleram.ai',
      home: 'https://circleram.ai',
      pricing: 'https://circleram.ai/pricing',
      talkToSales: 'https://www.circleram.ai/talk-to-sales',
      terms: 'https://circleram.com/terms.aspx',
      privacy: 'https://circleram.com/privacy.aspx',
      refund: 'https://circleram.com/refund.aspx',
      aboutUs: 'https://circleram.com/aboutus.aspx',
      learn: 'https://cm.circleram.com/home.aspx',
      blog: 'https://cm.circleram.com/support/blog',
      verticalDance: 'https://circleram.ai/verticals/dance',
      verticalCorporate: 'https://circleram.ai/verticals/corporate',
      verticalLearning: 'https://circleram.ai/verticals/learning',
      verticalFitness: 'https://circleram.ai/verticals/fitness'
    },
    social: {
      x: 'https://x.com/Circleram2',
      linkedin: 'https://www.linkedin.com/company/circleram/',
      facebook: 'https://www.facebook.com/circleram2',
      youtube: 'https://www.youtube.com/channel/UCSyBtSk3P0c_rpi_hHRMl1A',
      instagram: 'https://www.instagram.com/circleram2'
    },
    company: {
      address: '2603 Camino Ramon, Ste. 200, San Ramon, CA 94583',
      supportEmail: 'support@circleram.com',
      salesEmail: 'learn@circleram.com'
    },
    // Base domain tenant shops are hosted under, e.g. <shopalias>.circleram.ai
    tenantBaseDomain: 'circleram.ai'
  },

  // ---- PLACEHOLDER: real SchoolNode values arrive with #11 / #26 ----
  sn: {
    code: 'sn',
    name: 'SchoolNode',
    legalName: 'SchoolNode',
    productName: 'SchoolNode Course Manager',
    superadminTitle: 'SchoolNode | Superadmin',
    logo: {
      // SN branch ships SN-logo.png; it has no separate white/small variant, so reuse it.
      main: 'assets/images/SN-logo.png',
      white: 'assets/images/SN-logo.png',
      small: 'assets/images/SN-logo.png',
      favicon: 'assets/images/favicon.png'
    },
    links: {
      site: 'https://www.schoolnode.ai',
      home: 'https://schoolnode.ai',
      pricing: 'https://schoolnode.ai/pricing',
      talkToSales: 'https://www.schoolnode.ai/talk-to-sales',
      terms: 'https://schoolnode.ai/terms',
      privacy: 'https://schoolnode.ai/privacy',
      refund: 'https://schoolnode.ai/refund',
      aboutUs: 'https://schoolnode.ai/aboutus',
      learn: 'https://schoolnode.ai/home',
      blog: 'https://schoolnode.ai/support/blog',
      verticalDance: 'https://schoolnode.ai/verticals/dance',
      verticalCorporate: 'https://schoolnode.ai/verticals/corporate',
      verticalLearning: 'https://schoolnode.ai/verticals/learning',
      verticalFitness: 'https://schoolnode.ai/verticals/fitness'
    },
    social: {
      x: '',
      linkedin: '',
      facebook: '',
      youtube: '',
      instagram: ''
    },
    company: {
      address: '',
      supportEmail: 'support@schoolnode.ai',
      salesEmail: 'learn@schoolnode.ai'
    },
    tenantBaseDomain: 'schoolnode.ai'
  },

  // ---- PLACEHOLDER: real Sikh Scholar Hub values arrive with #11 / #26 ----
  ssh: {
    code: 'ssh',
    name: 'Sikh Scholar Hub',
    legalName: 'Sikh Scholar Hub',
    productName: 'Sikh Scholar Hub Course Manager',
    superadminTitle: 'Sikh Scholar Hub | Superadmin',
    logo: {
      // SSH branch ships SSH-logo.png + SSH-logo-white.png (no small variant, reuse main).
      main: 'assets/images/SSH-logo.png',
      white: 'assets/images/SSH-logo-white.png',
      small: 'assets/images/SSH-logo.png',
      favicon: 'assets/images/favicon.png'
    },
    links: {
      site: 'https://www.sikhscholarhub.com',
      home: 'https://sikhscholarhub.com',
      pricing: 'https://sikhscholarhub.com/pricing',
      talkToSales: 'https://www.sikhscholarhub.com/talk-to-sales',
      terms: 'https://sikhscholarhub.com/terms',
      privacy: 'https://sikhscholarhub.com/privacy',
      refund: 'https://sikhscholarhub.com/refund',
      aboutUs: 'https://sikhscholarhub.com/aboutus',
      learn: 'https://sikhscholarhub.com/home',
      blog: 'https://sikhscholarhub.com/support/blog',
      verticalDance: 'https://sikhscholarhub.com/verticals/dance',
      verticalCorporate: 'https://sikhscholarhub.com/verticals/corporate',
      verticalLearning: 'https://sikhscholarhub.com/verticals/learning',
      verticalFitness: 'https://sikhscholarhub.com/verticals/fitness'
    },
    social: {
      x: '',
      linkedin: '',
      facebook: '',
      youtube: '',
      instagram: ''
    },
    company: {
      address: '',
      supportEmail: 'support@sikhscholarhub.com',
      salesEmail: 'learn@sikhscholarhub.com'
    },
    tenantBaseDomain: 'sikhscholarhub.com'
  }
}
